import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

// mixpanel-browser is intentionally NOT imported statically. It is ~77 KiB and
// ships its own ES5 transforms (Array.from / isArray), so it dominated main.js's
// "unused JavaScript" and "legacy JavaScript" Lighthouse findings. We load it
// lazily on the first tracked event, so it lands in its own chunk off the
// initial critical path.
/** where an action happened: the current page and the one before it (label + its slug / product) */
export interface PageContext {
  page: string;
  pageKey: string;
  from: string;
  fromKey: string;
}

@Injectable( {
  providedIn: 'root',
} )
export class AnalyticsService {

  private mixpanel: any = null;
  private loading: Promise<any> | null = null;

  // The page the visitor is on and the page that led there, kept by SeoService on
  // every page view, so an action event can carry its context: a Cart Item Added
  // after blog post -> theme page reads Page "Theme", Page Key "vienna", From "Blog",
  // From Key "anatomy-of-the-paris-theme". Mixpanel only; nothing is shown.
  private context: PageContext = { page: '', pageKey: '', from: '', fromKey: '' };
  // The URL the context describes (the navigation's target, as the guard saw it).
  private contextUrl: { path: string; search: string } | null = null;

  setPageContext( context: PageContext, url: string ): void {
    this.context = { ...context };
    this.contextUrl = AnalyticsService.splitUrl( url );
  }

  pageContext(): PageContext {
    // A navigation that only changes the query (theme to theme on /theme) reuses
    // the route, so the guard that reports page views, and sets this context,
    // does not run. Catch up here: same path, new query means the same kind of
    // page with a new key, reached from the page the context still describes.
    const here = AnalyticsService.currentUrl();
    if ( here && this.contextUrl && here.path === this.contextUrl.path && here.search !== this.contextUrl.search ) {
      const query = new URLSearchParams( here.search );
      const key = query.get( 'product' ) || query.get( 'slug' ) || '';
      if ( key !== this.context.pageKey ) {
        this.context = { page: this.context.page, pageKey: key, from: this.context.page, fromKey: this.context.pageKey };
      }
      this.contextUrl = here;
    }
    return { ...this.context };
  }

  private static splitUrl( url: string ): { path: string; search: string } {
    const bare = ( url || '' ).split( '#' )[ 0 ];
    const at = bare.indexOf( '?' );
    return at < 0 ? { path: bare || '/', search: '' } : { path: bare.slice( 0, at ) || '/', search: bare.slice( at ) };
  }

  private static currentUrl(): { path: string; search: string } | null {
    try {
      return { path: window.location.pathname || '/', search: window.location.search || '' };
    } catch ( e ) {
      return null;
    }
  }

  private get enabled(): boolean {
    return !!environment.analytics.mixPanel.tag;
  }

  logEvent( event: string, attributes: any ): void {
    // Our custom event properties are sent with Title-Cased keys (e.g.
    // `selection` -> `Selection`, `internalNav` -> `Internal Nav`) so they read
    // cleanly in the Mixpanel UI. Reserved Mixpanel keys ($...) are left as-is.
    const props = this.titleCaseKeys( attributes );
    if ( !this.enabled ) {
      console.log( 'Event: ', event, props );
      return;
    }
    // Events fired before the bundle finishes loading queue on the promise.
    this.ready().then( ( mp ) => mp && mp.track( event, props ) );
  }

  /**
   * this browser's Mixpanel id ("$device:<uuid>", or the identified id), for the store's checkout
   * request: the server's order events name it, so the order lands on the same person in Mixpanel
   * as the visit that led to it (owner, 2026-09-21). null while analytics is off or the SDK fails.
   */
  distinctId(): Promise<string | null> {
    if ( !this.enabled ) {
      return Promise.resolve( null );
    }
    return this.ready().then( ( mp ) => {
      try {
        const id = mp ? mp.get_distinct_id() : null;
        return typeof id === 'string' && id ? id : null;
      } catch ( e ) {
        return null;
      }
    } ).catch( () => null );
  }

  private titleCaseKeys( attributes: any ): any {
    if ( !attributes || typeof attributes !== 'object' || Array.isArray( attributes ) ) {
      return attributes;
    }
    const out: any = {};
    for ( const key of Object.keys( attributes ) ) {
      const reserved = key.startsWith( '$' );
      const mapped = reserved ? key : AnalyticsService.titleCaseKey( key );
      out[ mapped ] = reserved ? attributes[ key ] : AnalyticsService.capitalizeValue( attributes[ key ] );
    }
    return out;
  }

  // Property VALUES read better in Mixpanel when they start with a capital letter
  // (e.g. a scheme "featured" -> "Featured"). Only the leading letter is changed,
  // and a value is left untouched when it is a URL / path ("/"), a hex code ("#"),
  // already multi-word text (contains a space), or does not start with a lower-case
  // letter - so links, colours and proper names like "GitHub" are never mangled.
  private static capitalizeValue( value: any ): any {
    if ( typeof value !== 'string' || /[/#\s]/.test( value ) || !/^[a-z]/.test( value ) ) {
      return value;
    }
    return value.charAt( 0 ).toUpperCase() + value.slice( 1 );
  }

  private static titleCaseKey( key: string ): string {
    return key
      .replace( /[_-]+/g, ' ' )                  // snake_case / kebab-case -> spaces
      .replace( /([a-z0-9])([A-Z])/g, '$1 $2' )  // camelCase -> spaced words
      .replace( /\s+/g, ' ' )
      .trim()
      .replace( /\b\w/g, ( c ) => c.toUpperCase() );
  }

  private ready(): Promise<any> {
    if ( this.mixpanel ) {
      return Promise.resolve( this.mixpanel );
    }
    if ( !this.loading ) {
      this.loading = import( 'mixpanel-browser' ).then( ( mod ) => {
        const mp = mod.default;
        mp.init( environment.analytics.mixPanel.tag, {
          api_host: environment.analytics.mixPanel.endpoint,
        } );
        this.mixpanel = mp;
        return mp;
      } );
    }
    return this.loading;
  }
}
